import React from "react";

import { useMutation } from "@apollo/client";
import { Box, Button } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";

import Visibility from "@/components/layout/Visibility";
import { displayError } from "@/utils/errors";

import { recipesPageQuery } from "../Index/RecipesPage";
import {
  createRecipeMutation,
  updateRecipeMutation,
  useRecipe,
} from "../store/RecipeStore";

interface RecipeFormFooterProps {
  onCancel: () => void;
}

const RecipeFormFooter: React.FC<RecipeFormFooterProps> = ({ onCancel }) => {
  const recipe = useRecipe();
  const navigate = useNavigate();

  const [createRecipe] = useMutation(createRecipeMutation);
  const [updateRecipe] = useMutation(updateRecipeMutation);

  const handleCreateRecipe = async (): Promise<void> => {
    recipe.submittingForm(true);
    try {
      await createRecipe({
        variables: {
          input: recipe.toCreateRecipeInput(),
        },
        refetchQueries: [recipesPageQuery],
      });

      navigate("/database/recipes");
    } catch (error) {
      alert({
        title: "Failed to add recipe",
        message: displayError(error),
      });
    }
    recipe.submittingForm(false);
  };

  const handleUpdateRecipe = async (): Promise<void> => {
    recipe.submittingForm(true);
    try {
      await updateRecipe({
        variables: {
          input: recipe.toUpdateRecipeInput(),
        },
        refetchQueries: [recipesPageQuery],
      });

      navigate("/database/recipes");
    } catch (error) {
      alert({
        title: "Failed to add recipe",
        message: displayError(error),
      });
    }
    recipe.submittingForm(false);
  };

  return (
    <Box py={4} px={6}>
      <Box display="flex" width="100%">
        <Box flex={1}>
          {/* TODO */}
          <Visibility visible={recipe.step.hasPreviousStep}>
            <Button variant="outlined" onClick={recipe.step.previousStep}>
              Back
            </Button>
          </Visibility>
        </Box>
        <Box flex={1} display="flex" justifyContent="center">
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Box ml={3}>
            {recipe.step.hasNextStep ? (
              <Button
                key="next"
                disabled={!recipe.isCompleted}
                onClick={recipe.step.nextStep}
              >
                Next
              </Button>
            ) : (
              <Button
                // Provide a `key` prop so that form won't submit immediately
                // after clicking Next button.
                key="submit"
                type="submit"
                disabled={
                  !recipe.isCompleted ||
                  recipe.isSubmittingForm ||
                  recipe.hasEmptyInstruction
                }
                onClick={
                  !recipe.isEditingForm
                    ? handleCreateRecipe
                    : handleUpdateRecipe
                }
              >
                {recipe.isEditingForm ? "Save" : "Create recipe"}
              </Button>
            )}
          </Box>
        </Box>
        <Box flex={1} />
      </Box>
    </Box>
  );
};

export default observer(RecipeFormFooter);
